import React from "react";
import Loader from "./Loader";

const Button = ({
  primary,
  success,
  warning,
  error,
  icon,
  iconLeft,
  label,
  onClick,
  link,
  secondary,
  className,
  children,
  iconOnly,
  size,
  variant = "neutral",
  as = "a",
  // Control icon placement relative to label in standard icon mode
  iconPosition = "left", // left | right
  // Image-only / media button support (no inline styles)
  imageSrc,
  imageAlt,
  imageSize = "md", // xs | sm | md | lg | xl
  labelPosition = "right", // right | left | under | over
  isLoading = false,
  loadingLabel,
  ...rest
}) => {
  const classNames = ["btn"];
  const tone = success
    ? "success"
    : warning
      ? "warning"
      : error
        ? "error"
        : "primary";

  const isPrimary = Boolean(primary || variant === "primary");

  if (isPrimary) {
    classNames.push("btn--primary", `${tone}-fill`);
  }

  if (secondary) {
    // Use `.btn-secondary` to match SCSS and ensure outlined border + primary color
    classNames.push("btn-secondary", `${tone}-outline`);
  }

  if (link) {
    classNames.push("btn-link");
  }

  if (iconOnly) {
    classNames.push("btn-icon-only");
  }

  if (size) {
    classNames.push(`btn-${size}`);
  }

  // Media mode: background image instead of icon
  const isImageMode = Boolean(imageSrc);
  if (isImageMode) {
    classNames.push("btn-img");
    // placement modifier
    classNames.push(`btn-label-${labelPosition}`);
  } else {
    // Standard icon/text mode: allow icon on left or right
    if (!iconOnly) {
      if (iconPosition === "right") {
        classNames.push("btn-icon-right");
      } else {
        classNames.push("btn-icon-left");
      }
    }
  }

  if (className) {
    classNames.push(className);
  }

  const Tag = as;
  const extraProps = {};
  if (Tag === "button" && !("type" in (rest || {}))) {
    extraProps.type = "button";
  }
  const effectiveIcon = iconLeft ?? icon;
  // Guard against non-function onClick to avoid React warnings
  const safeOnClick = typeof onClick === "function" ? onClick : undefined;
  return (
    <>
      <Tag
        className={classNames.join(" ").trim()}
        onClick={safeOnClick}
        {...extraProps}
        {...rest}
      >
        {isLoading ? (
          <div data-layer="Content" className="content">
            <Loader
              className="btn-loader"
              ariaLabel={loadingLabel || label || "Loading"}
            />
          </div>
        ) : (
          <div
            data-layer="Content"
            className={`content ${!label ? "icon-only" : ""}`}
          >
            {isImageMode ? (
              <>
                <img
                  className={[`btn-img-${imageSize}`].join(" ")}
                  src={imageSrc}
                  alt={imageAlt || ""}
                  title={imageAlt || ""}
                />
                {label && (
                  <label
                    data-layer="Default"
                    className={`label ${size === "lg" ? "t-btn-lg" : "t-btn-md"}`}
                  >
                    {label}
                  </label>
                )}
              </>
            ) : (
              <>
                {label && (
                  <div
                    data-layer="Default"
                    className={`label ${size === "lg" ? "t-btn-lg" : "t-btn-md"}`}
                  >
                    {label}
                  </div>
                )}
                {effectiveIcon && (
                  <div data-layer="Default" className="icon material-icons">
                    {effectiveIcon}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Tag>
      {/* Container for additional content, outside the <a> */}
      {children}
    </>
  );
};

export default Button;
